import { Clock, CheckCircle, XCircle, Copy, Check, Settings, ChevronDown } from "lucide-react";
import { useState } from "react";

const WithdrawalsTab = ({ withdrawals, onWithdrawalStatus }) => {
  const [copiedId, setCopiedId] = useState(null);
  const [manageDropdown, setManageDropdown] = useState(null);

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleStatusChange = (withdrawalId, newStatus) => {
    onWithdrawalStatus(withdrawalId, newStatus);
    setManageDropdown(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center gap-1.5 bg-blue-500/15 text-blue-400 px-3 py-1.5 rounded text-sm font-medium">
            <Clock size={14} />
            В обработке
          </span>
        );
      case "completed":
        return (
          <span className="inline-flex items-center gap-1.5 bg-[#34D399]/15 text-[#34D399] px-3 py-1.5 rounded text-sm font-medium">
            <CheckCircle size={14} />
            Выполнено
          </span>
        );
      case "rejected":
        return (
          <span className="inline-flex items-center gap-1.5 bg-[#F87171]/15 text-[#F87171] px-3 py-1.5 rounded text-sm font-medium">
            <XCircle size={14} />
            Отклонено
          </span>
        );
      default:
        return status;
    }
  };

  return (
    <div>
      <h2 className="font-chivo font-bold text-xl text-white mb-6">Заявки на вывод</h2>
      <div className="bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="admin-withdrawals-table">
            <thead>
              <tr className="border-b border-[#1F1F1F]">
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Дата</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Пользователь</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Сеть</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Сумма</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Адрес кошелька</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Статус</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Управление</th>
              </tr>
            </thead>
            <tbody>
              {withdrawals.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-[#A1A1AA]">
                    Нет заявок
                  </td>
                </tr>
              ) : (
                withdrawals.map((w) => (
                  <tr key={w.id} className="border-b border-[#1F1F1F]/50 hover:bg-[#1A1A1A]/50">
                    <td className="py-4 px-4 text-[#A1A1AA] text-sm">
                      {formatDate(w.created_at)}
                    </td>
                    <td className="py-4 px-4 text-white">{w.user_email}</td>
                    <td className="py-4 px-4 text-white">{w.asset}</td>
                    <td className="py-4 px-4 font-mono text-white">${w.amount.toFixed(2)}</td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <code className="bg-[#000000] text-white px-3 py-1.5 rounded text-sm font-mono break-all max-w-[250px]">
                          {w.wallet_address}
                        </code>
                        <button
                          onClick={() => copyToClipboard(w.wallet_address, w.id)}
                          className={`p-1.5 rounded transition-colors flex-shrink-0 ${
                            copiedId === w.id 
                              ? 'bg-[#34D399]/20 text-[#34D399]' 
                              : 'bg-[#1F1F1F] text-[#A1A1AA] hover:text-white'
                          }`}
                          title={copiedId === w.id ? "Скопировано!" : "Копировать адрес"}
                        >
                          {copiedId === w.id ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {getStatusBadge(w.status)}
                    </td>
                    <td className="py-4 px-4">
                      <div className="relative">
                        <button
                          onClick={() => setManageDropdown(manageDropdown === w.id ? null : w.id)}
                          className="flex items-center gap-2 bg-[#1F1F1F] hover:bg-[#3E3E3E] text-white px-3 py-2 rounded transition-colors"
                        >
                          <Settings size={16} />
                          <span className="text-sm">Управление</span>
                          <ChevronDown size={14} className={`transition-transform ${manageDropdown === w.id ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {manageDropdown === w.id && (
                          <>
                            <div 
                              className="fixed inset-0 z-40" 
                              onClick={() => setManageDropdown(null)}
                            />
                            <div className="absolute right-0 top-full mt-1 w-48 bg-[#1A1A1A] border border-[#1F1F1F] rounded-lg shadow-xl z-50 overflow-hidden">
                              <div className="p-2 border-b border-[#1F1F1F]">
                                <span className="text-[#A1A1AA] text-xs">Изменить статус:</span>
                              </div>
                              
                              <button
                                onClick={() => handleStatusChange(w.id, "pending")}
                                className={`w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors ${
                                  w.status === "pending" 
                                    ? 'bg-blue-500/20 text-blue-400' 
                                    : 'text-white hover:bg-[#1F1F1F]'
                                }`}
                              >
                                <Clock size={16} className="text-blue-400" />
                                <span>В обработке</span>
                                {w.status === "pending" && <Check size={14} className="ml-auto" />}
                              </button>
                              
                              <button
                                onClick={() => handleStatusChange(w.id, "completed")}
                                className={`w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors ${
                                  w.status === "completed" 
                                    ? 'bg-[#34D399]/20 text-[#34D399]' 
                                    : 'text-white hover:bg-[#1F1F1F]'
                                }`}
                              >
                                <CheckCircle size={16} className="text-[#34D399]" />
                                <span>Выполнено</span>
                                {w.status === "completed" && <Check size={14} className="ml-auto" />}
                              </button>
                              
                              <button
                                onClick={() => handleStatusChange(w.id, "rejected")}
                                className={`w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors ${
                                  w.status === "rejected" 
                                    ? 'bg-[#F87171]/20 text-[#F87171]' 
                                    : 'text-white hover:bg-[#1F1F1F]'
                                }`}
                              >
                                <XCircle size={16} className="text-[#F87171]" />
                                <span>Отклонено</span>
                                {w.status === "rejected" && <Check size={14} className="ml-auto" />}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalsTab;
